import type { ReactNode } from 'react';

/**
 * A strip that moves across the page by itself.
 *
 * The list is drawn twice end to end and the track slides left by exactly half
 * its width, so the loop has no seam. It moves with a CSS transform only (no
 * timer, no layout), pauses while the pointer or keyboard focus is on it, and
 * on a touch screen holds still while a finger is down. For anyone who has asked
 * for reduced motion it does not move at all: it becomes an ordinary row that
 * swipes and scrolls, which is why the second copy is hidden from assistive
 * technology and only exists to make the loop.
 *
 * The caller supplies each card, so the same strip carries product screens and
 * property photographs.
 */
export function Marquee<T>({ items, render, seconds = 60, label, className = '' }: {
  items: T[]; render: (item: T, index: number) => ReactNode;
  /** Time for one full pass of the list. Longer is calmer. */
  seconds?: number; label: string; className?: string;
}) {
  return (
    <div className={`mq ${className}`} role="region" aria-label={label} tabIndex={0}>
      <div className="mq-track" style={{ ['--mq-s' as string]: `${seconds}s` }}>
        <ul className="mq-list">
          {items.map((it, i) => <li key={i} className="mq-item">{render(it, i)}</li>)}
        </ul>
        <ul className="mq-list" aria-hidden="true">
          {items.map((it, i) => <li key={i} className="mq-item">{render(it, i + items.length)}</li>)}
        </ul>
      </div>
    </div>
  );
}
